/**
 * Costed Job References — the arithmetic behind the table.
 *
 * Two numbers per indexed line: what the original costing sheet QUOTED for
 * labor (labor_per_unit, plus the per-department split) and what the shop
 * ACTUALLY clocked in Island Time (actual_hours_per_unit, plus the
 * actual_labor_detail payload the attach script copied off the Island Time
 * export). The ratio between them is the cushion: how many times over the
 * quote covered the real work.
 *
 * Nothing here prices anything — no rate, no dollar. It is display maths only,
 * kept pure so the classification is testable without a DOM.
 */

export const DEPTS = ["cnc", "cut", "fab", "paint"];

export const DEPT_LABELS = {
  cnc: "CNC",
  cut: "Cut",
  fab: "Fab",
  paint: "Paint",
};

/** A quote with 1.15× or more of the real hours had room in it. */
export const CUSHION_COMFORTABLE = 1.15;
/**
 * The tolerance floor. From 0.95× up to 1.15× the quote lands within 5% of
 * break-even on the low side — close enough that measurement noise, not
 * cushion, explains the gap. It does NOT mean the quote covered the work:
 * anything under 1.00× is already short. Below 0.95× the shortfall is bigger
 * than the tolerance and the line reads as an outright under-quote.
 */
export const CUSHION_UNDER = 0.95;

export const SCOPES = [
  { value: "est", label: "Historic estimate" },
  { value: "act", label: "Actual (Island Time)" },
  { value: "both", label: "Both" },
];

export const SCOPE_HINTS = {
  est: "Showing the labor quoted on the original costing sheet.",
  act: "Showing real shop hours clocked in Island Time. Lines with no match are marked.",
  both: "Quoted and actual side by side, with the cushion between them.",
};

/** Coerce anything the API hands us into a finite number, or null. */
export function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/** Quantity the line was costed at; never zero, so divisions stay safe. */
export function lineQuantity(item) {
  const qty = toNumber(item && item.quantity);
  return qty && qty > 0 ? qty : 1;
}

/** Hours the shop actually clocked, per unit — null when nothing was logged. */
export function actualHoursPerUnit(item) {
  const hours = toNumber(item && item.actual_hours_per_unit);
  return hours !== null && hours > 0 ? hours : null;
}

export function hasActuals(item) {
  return actualHoursPerUnit(item) !== null;
}

/** Hours the sheet quoted, per unit. */
export function quotedHoursPerUnit(item) {
  const hours = toNumber(item && item.labor_per_unit);
  return hours !== null && hours > 0 ? hours : null;
}

/**
 * Cushion = quoted ÷ actual. 4.2 means the quote carried 4.2× the hours the
 * shop needed. Null whenever either side of the division is missing.
 */
export function cushionRatio(item) {
  const quoted = quotedHoursPerUnit(item);
  const actual = actualHoursPerUnit(item);
  if (quoted === null || actual === null) return null;
  return quoted / actual;
}

/**
 * Three states, three colours: room in the quote, within tolerance of
 * break-even, or an outright under-quote.
 */
export function classifyCushion(ratio) {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
    return null;
  }
  if (ratio >= CUSHION_COMFORTABLE) return "good";
  if (ratio >= CUSHION_UNDER) return "tight";
  return "under";
}

export function formatCushion(ratio) {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
    return "—";
  }
  return `${ratio.toFixed(2)}×`;
}

const CUSHION_WORDS = {
  good: "cushion",
  // Not "covered" — 0.95–0.999 is still short, just inside the tolerance.
  tight: "near break-even",
  under: "under-quoted",
};

export function cushionLabel(ratio) {
  const tone = classifyCushion(ratio);
  if (!tone) return "—";
  return `${formatCushion(ratio)} ${CUSHION_WORDS[tone]}`;
}

/** Hours per unit either side quoted/spent, as a plain difference. */
export function cushionNote(item) {
  const quoted = quotedHoursPerUnit(item);
  const actual = actualHoursPerUnit(item);
  if (quoted === null || actual === null) return null;
  const gap = quoted - actual;
  if (Math.abs(gap) < 0.005) return "quote matched the hours exactly";
  return gap > 0
    ? `quoted ${formatHours(gap)} hrs over`
    : `short ${formatHours(Math.abs(gap))} hrs per unit`;
}

export function formatHours(value) {
  const numeric = toNumber(value);
  if (numeric === null) return "—";
  return numeric.toFixed(2);
}

/**
 * Quoted department hours per unit — the index already stores them that way
 * (historical_line_item.py declares the per-unit contract; the 2026JD corpus
 * honors it, and legacy dept values are documented as unreliable either way).
 */
export function quotedDeptPerUnit(item) {
  const out = {};
  DEPTS.forEach((dept) => {
    out[dept] = toNumber(item && item[`${dept}_hours`]);
  });
  return out;
}

/** Quantity Island Time attributed the clocked hours to. */
export function detailQuantity(detail) {
  const qty = toNumber(detail && detail.qty);
  return qty && qty > 0 ? qty : null;
}

/**
 * Actual department hours per unit: the Island Time department total divided
 * by the quantity that shift window covered. Null-safe — a line can carry a
 * headline actual with no department breakdown at all.
 */
export function actualDeptPerUnit(detail) {
  const qty = detailQuantity(detail);
  const out = {};
  DEPTS.forEach((dept) => {
    const total = toNumber(detail && detail[`${dept}_hours`]);
    out[dept] = total === null || qty === null ? null : total / qty;
  });
  return out;
}

/** "2023-03-27 11:30:04" → "Mar 27, 2023". Parsed by hand: no timezone drift. */
export function formatShiftDate(value) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!match) return String(value);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[Number(match[2]) - 1];
  if (!month) return String(value);
  return `${month} ${Number(match[3])}, ${match[1]}`;
}

/** The Island Time shift window for a line, or null when we have none. */
export function shiftWindow(detail) {
  const first = formatShiftDate(detail && detail.first_shift);
  const last = formatShiftDate(detail && detail.last_shift);
  if (!first && !last) return null;
  if (first && last && first !== last) return `${first} → ${last}`;
  return first || last;
}

/**
 * How much the Island Time figure behind a cushion can be trusted.
 *
 * The attach script does not always know the unit count a shift window
 * covered: sometimes it guessed the quantity off the job description
 * (`qty_confidence` says how), and sometimes one job-level total was written
 * onto several line items at once (`shared_across_lines` counts them). Both
 * cases make the *per-unit* actual — and so the cushion derived from it — an
 * estimate rather than a measurement, and neither may be dressed up as an
 * authoritative verdict.
 *
 * The two caveats are INDEPENDENT — a line can rest on a guessed quantity AND
 * be carrying a shared job-level total, and saying only the first would quietly
 * drop the second. `cushionEvidenceFlags` is therefore the full truth: every
 * flag that applies, in order of how loudly it needs saying.
 *
 * Returns ['unverified-qty'?, 'shared'?] — empty when the figure was measured.
 */
export function cushionEvidenceFlags(item) {
  const detail = (item && item.actual_labor_detail) || null;
  const flags = [];
  const confidence = detail && detail.qty_confidence;
  if (
    confidence !== null &&
    confidence !== undefined &&
    confidence !== "" &&
    confidence !== "parsed"
  ) {
    flags.push("unverified-qty");
  }
  const shared = toNumber(detail && detail.shared_across_lines);
  if ((shared === null ? 1 : shared) > 1) flags.push("shared");
  return flags;
}

/**
 * The single-value view, for the places that can only show one word: the
 * guessed quantity outranks the shared total, because a wrong unit count moves
 * the per-unit number itself rather than just widening what it covers.
 *
 * Returns 'verified' | 'unverified-qty' | 'shared'.
 */
export function cushionEvidence(item) {
  const flags = cushionEvidenceFlags(item);
  return flags.length > 0 ? flags[0] : "verified";
}

/** True when the line's actual hours were measured, not inferred. */
export function isVerifiedCushion(item) {
  return cushionEvidenceFlags(item).length === 0;
}

/** How many line items one job-level actual was written onto (1 = just this one). */
export function sharedAcrossLines(item) {
  const shared = toNumber(
    item &&
      item.actual_labor_detail &&
      item.actual_labor_detail.shared_across_lines,
  );
  return shared === null ? 1 : shared;
}

/** Where a guessed quantity came from, for the warning copy. */
export function qtySource(item) {
  const source =
    item && item.actual_labor_detail && item.actual_labor_detail.qty_source;
  return source ? String(source) : "unknown";
}

/** How many of a result set have shop hours attached. */
export function countWithActuals(items) {
  return (items || []).filter(hasActuals).length;
}

/** Hours per unit the quote carried over the shop's real time (negative = short). */
export function cushionGap(item) {
  const quoted = quotedHoursPerUnit(item);
  const actual = actualHoursPerUnit(item);
  if (quoted === null || actual === null) return null;
  return quoted - actual;
}

/**
 * Median cushion across the lines that have one. The median, not the mean:
 * one 12× outlier off a tiny bracket should not tell the owner his whole
 * corpus is padded.
 *
 * Lines whose actual hours rest on a guessed quantity or a shared job-level
 * total are excluded by default — a headline median is exactly the number
 * that must not be propped up by figures we cannot stand behind. Pass
 * `{ includeUnverified: true }` to median the whole set anyway.
 */
export function medianCushion(items, { includeUnverified = false } = {}) {
  const ratios = (items || [])
    .filter((item) => includeUnverified || isVerifiedCushion(item))
    .map(cushionRatio)
    .filter((r) => r !== null)
    .sort((a, b) => a - b);
  if (ratios.length === 0) return null;
  const mid = Math.floor(ratios.length / 2);
  return ratios.length % 2 === 1
    ? ratios[mid]
    : (ratios[mid - 1] + ratios[mid]) / 2;
}
